/**
 * DetailStore - Event-driven state management for active detail item
 *
 * Manages the currently selected/viewed item for the detail panel.
 * When a user clicks a card, the detail is set here and the panel updates.
 *
 * Usage:
 * ```typescript
 * const store = getDetailStore();
 * store.on('change', (item) => updateDetailPanel(item));
 * store.setActiveItem({ id: '1', title: 'BMW M4', ... });
 * ```
 */

import type { CardAction } from '../ui/blocks/types';

export interface DetailItem {
  id: string;
  title: string;
  description?: string;
  image?: string;
  images?: string[];        // Multiple images for carousel
  badge?: string;
  price?: string;
  metadata?: Record<string, string>;
  stats?: { label: string; value: string; icon?: string }[];
  features?: string[];
  action?: CardAction;
  actions?: CardAction[];   // Multiple actions
  sourceBlockType?: string; // Which block it came from ('cards', 'browse', etc.)
}

export type DetailEvent = 'change' | 'clear';

export type DetailEventHandler = (item: DetailItem | null) => void;

/** Max change events per second before throttling */
const MAX_CHANGES_PER_SECOND = 10;

export class DetailStore {
  private activeItem: DetailItem | null = null;
  private listeners: Map<DetailEvent, Set<DetailEventHandler>> = new Map();
  private history: DetailItem[] = [];
  private maxHistory: number = 10;

  // Safety: throttle rapid changes to prevent infinite loops
  private changeCount: number = 0;
  private changeResetTimeout: ReturnType<typeof setTimeout> | null = null;
  private isThrottled: boolean = false;

  constructor() {
    this.listeners.set('change', new Set());
    this.listeners.set('clear', new Set());
  }

  /**
   * Subscribe to detail events.
   */
  on(event: DetailEvent, handler: DetailEventHandler): () => void {
    this.listeners.get(event)?.add(handler);
    return () => this.off(event, handler);
  }

  /**
   * Unsubscribe from detail events.
   */
  off(event: DetailEvent, handler: DetailEventHandler): void {
    this.listeners.get(event)?.delete(handler);
  }

  /**
   * Emit an event to all listeners.
   */
  private emit(event: DetailEvent): void {
    this.listeners.get(event)?.forEach((handler) => {
      try {
        handler(this.activeItem);
      } catch (error) {
        console.error(`[DetailStore] Error in ${event} handler:`, error);
      }
    });
  }

  /**
   * Set the active detail item.
   * Protected by throttling to prevent infinite loops.
   */
  setActiveItem(item: DetailItem): void {
    // Safety: throttle rapid changes
    if (this.isThrottled) {
      console.warn('[DetailStore] Throttled - ignoring setActiveItem');
      return;
    }

    this.changeCount++;
    if (this.changeCount > MAX_CHANGES_PER_SECOND) {
      console.error('[DetailStore] Too many changes per second - throttling');
      this.isThrottled = true;
      // Auto-reset after 2 seconds
      setTimeout(() => {
        this.isThrottled = false;
        this.changeCount = 0;
      }, 2000);
      return;
    }

    // Reset counter after 1 second of no changes
    if (this.changeResetTimeout) {
      clearTimeout(this.changeResetTimeout);
    }
    this.changeResetTimeout = setTimeout(() => {
      this.changeCount = 0;
    }, 1000);

    // Add current item to history before switching
    if (this.activeItem && this.activeItem.id !== item.id) {
      this.history.unshift(this.activeItem);
      if (this.history.length > this.maxHistory) {
        this.history.pop();
      }
    }

    this.activeItem = { ...item };
    this.emit('change');
  }

  /**
   * Clear the active detail item.
   */
  clearActiveItem(): void {
    if (!this.activeItem) return;

    this.activeItem = null;
    this.emit('clear');
    this.emit('change');
  }

  /**
   * Get the active detail item.
   */
  getActiveItem(): DetailItem | null {
    return this.activeItem ? { ...this.activeItem } : null;
  }

  /**
   * Check if there's an active item.
   */
  hasActiveItem(): boolean {
    return this.activeItem !== null;
  }

  /**
   * Go back to previous item in history.
   */
  goBack(): DetailItem | null {
    const previous = this.history.shift();
    if (previous) {
      this.activeItem = previous;
      this.emit('change');
      return previous;
    }
    return null;
  }

  /**
   * Check if there's history to go back to.
   */
  canGoBack(): boolean {
    return this.history.length > 0;
  }

  /**
   * Get viewing history.
   */
  getHistory(): DetailItem[] {
    return [...this.history];
  }

  /**
   * Clear all history.
   */
  clearHistory(): void {
    this.history = [];
  }

  /**
   * Clear all event listeners.
   * Called during reset to prevent memory leaks and stale references.
   */
  clearAllListeners(): void {
    this.listeners.get('change')?.clear();
    this.listeners.get('clear')?.clear();
    // Clear any pending timeout
    if (this.changeResetTimeout) {
      clearTimeout(this.changeResetTimeout);
      this.changeResetTimeout = null;
    }
  }

  /**
   * Generate summary text for AI context.
   * Useful when user wants to ask about the currently viewed item.
   */
  getSummaryForAI(): string {
    if (!this.activeItem) {
      return '';
    }

    const item = this.activeItem;
    let summary = `[CURRENTLY VIEWING]\n`;
    summary += `${item.title}`;

    if (item.badge) {
      summary += ` (${item.badge})`;
    }

    if (item.price) {
      summary += ` - ${item.price}`;
    }

    summary += '\n';

    if (item.description) {
      summary += `${item.description}\n`;
    }

    if (item.stats && item.stats.length > 0) {
      summary += 'Key specs: ';
      summary += item.stats.map(s => `${s.label}: ${s.value}`).join(', ');
      summary += '\n';
    }

    if (item.features && item.features.length > 0) {
      summary += `Features: ${item.features.join(', ')}\n`;
    }

    if (item.metadata && Object.keys(item.metadata).length > 0) {
      summary += 'Details: ';
      summary += Object.entries(item.metadata)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
      summary += '\n';
    }

    summary += '\nUser may ask questions about this specific item.';

    return summary;
  }
}

// Singleton instance for widget-wide state
let globalDetailStore: DetailStore | null = null;

/**
 * Get or create the global detail store instance.
 */
export function getDetailStore(): DetailStore {
  if (!globalDetailStore) {
    globalDetailStore = new DetailStore();
  }
  return globalDetailStore;
}

/**
 * Reset the global detail store (for testing or demo switching).
 * Clears all state and listeners to prevent memory leaks.
 */
export function resetDetailStore(): void {
  if (globalDetailStore) {
    globalDetailStore.clearActiveItem();
    globalDetailStore.clearHistory();
    // Clear all listeners to prevent stale references
    globalDetailStore.clearAllListeners();
  }
  globalDetailStore = null;
}
